import { useRef } from "react";

import { Trans } from "@lingui/react/macro";

import Button from "@mui/material/Button";
import MuiCard from "@mui/material/Card";
import Modal, { ModalProps } from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import styled from "@mui/material/styles/styled";
import Textarea from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import { ModalTitle } from "@readai/common/components/Modal";
import CopyIcon from "@readai/icons/uui/Copy01";

interface CopyDialogProps extends Omit<ModalProps, "children"> {
  copyValue: string;
}

const Card = styled(MuiCard)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "600px",
  maxWidth: "95%",
  maxHeight: "95%",
  padding: "24px",
});

const CopyDialog = ({ open, onClose, copyValue }: CopyDialogProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleRef = (instance: HTMLTextAreaElement | null) => {
    textAreaRef.current = instance;
    setTimeout(() => {
      textAreaRef.current?.focus();
      textAreaRef.current?.select();
    }, 50);
  };

  // Just avoids unnecessary markup in the document, while sacrificing the close animation
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Card>
        <Stack spacing="12px">
          <Stack direction="row" alignItems="center" spacing={1}>
            <CopyIcon />
            <ModalTitle sx={{ color: "text.default" }}>
              <Trans>Copy text</Trans>
            </ModalTitle>
          </Stack>
          <Stack spacing="4px">
            <Textarea
              style={{ width: "100%" }}
              minRows={3}
              maxRows={20}
              value={copyValue}
              ref={handleRef}
            />
            <Typography variant="disclaimer">
              <Trans>Press Ctrl+C (or Cmd+C on Mac) to copy</Trans>
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{ width: "100%" }}
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              onClick={(event) =>
                onClose ? onClose(event, "escapeKeyDown") : null
              }
            >
              <Trans>Close</Trans>
            </Button>
          </Stack>
        </Stack>
      </Card>
    </Modal>
  );
};

export default CopyDialog;
